// Close Tab Tool - Manages closing of file tabs in the editor
// Extends WorkspaceTool - has access to appType and toolSystem
// This is a frontend-only tool that doesn't interact with the filesystem
// but provides structured operations for tab management
// Accepts workspace UI references to directly update state

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class CloseTabTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'closeTabTool';
  }
  /**
   * Close a single tab
   * @param {string} fileId - File ID of the tab to close
   * @param {Array} openFiles - Array of open file objects
   * @param {string|null} activeFileId - Currently active file ID
   * @param {Set|Array} unsavedFiles - Set or Array of unsaved file IDs
   * @param {Function} setOpenFiles - React state setter for openFiles
   * @param {Function} setUnsavedFiles - React state setter for unsavedFiles
   * @param {Function} setActiveFileId - React state setter for activeFileId
   * @param {Object} manager - Workspace manager instance
   * @param {Function} onSaveFile - Optional callback to save a file (receives file object)
   * @param {boolean} force - If true, skip unsaved changes check
   * @returns {Promise<Object>} Result with operation status or needsConfirmation flag
   */
  async closeTab(
    fileId,
    openFiles = [],
    activeFileId = null,
    unsavedFiles = new Set(),
    setOpenFiles,
    setUnsavedFiles,
    setActiveFileId,
    manager,
    onSaveFile = null,
    force = false
  ) {
    try {
      // Validate fileId
      if (!fileId || typeof fileId !== "string") {
        return this.returnError({}, "Valid file ID required");
      }

      // Validate openFiles array
      if (!Array.isArray(openFiles)) {
        return this.returnError({}, "openFiles must be an array");
      }

      // Validate state setters
      if (typeof setOpenFiles !== "function") {
        return this.returnError({}, "setOpenFiles function is required");
      }
      if (typeof setUnsavedFiles !== "function") {
        return this.returnError({}, "setUnsavedFiles function is required");
      }
      if (typeof setActiveFileId !== "function") {
        return this.returnError({}, "setActiveFileId function is required");
      }

      // Convert unsavedFiles to Set if needed
      const unsavedFilesSet = unsavedFiles instanceof Set 
        ? unsavedFiles 
        : new Set(Array.isArray(unsavedFiles) ? unsavedFiles : []);

      const fileToClose = openFiles.find(
        (f) => f.id === fileId || f.stableId === fileId
      );
      if (!fileToClose) {
        return this.returnError(
          {},
          `File with ID ${fileId} not found in open files`
        );
      }

      // Check for unsaved changes (unless forced)
      const hasUnsavedChanges = !force && (
        unsavedFilesSet.has(fileId) || 
        unsavedFilesSet.has(fileToClose.stableId)
      );

      if (hasUnsavedChanges) {
        // Return indication that confirmation is needed
        return this.returnSuccess(
          {
            needsConfirmation: true,
            file: fileToClose,
            fileId,
            action: "close",
          },
          "File has unsaved changes"
        );
      }

      // Save file if callback provided and it's the active file
      if (onSaveFile && activeFileId === fileId && typeof onSaveFile === 'function') {
        try {
          await onSaveFile(fileToClose);
        } catch (error) {
          logger.warn(
            { tags: "tools|closetab|save", color1: "yellow" },
            `Failed to save file before closing: ${fileToClose.name}`,
            error
          );
        }
      }

      // Remove file from open files
      setOpenFiles((prev) => prev.filter((f) => f.id !== fileId));

      // Remove from unsaved files
      setUnsavedFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        // Also check stableId
        if (fileToClose.stableId) {
          newSet.delete(fileToClose.stableId);
        }
        return newSet;
      });

      // If closing active tab, switch to another tab
      if (activeFileId === fileId) {
        const remainingFiles = openFiles.filter((f) => f.id !== fileId);
        if (remainingFiles.length > 0) {
          const newActiveId = remainingFiles[remainingFiles.length - 1].id;
          setActiveFileId(newActiveId);
          const file = remainingFiles.find((f) => f.id === newActiveId);
          if (file && manager) {
            await manager.selectFile(file);
          }
        } else {
          setActiveFileId(null);
        }
      }

      logger.log(
        {
          tags: "tools|closetab|info",
          color1: "lime",
        },
        `Closing tab: ${fileToClose.name || fileId}`
      );

      return this.returnSuccess(
        {
          fileId,
          action: "close",
        },
        "Tab closed successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|closetab|error",
          color1: "red",
        },
        `Failed to close tab: ${fileId}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Close all tabs except the specified one
   * @param {string} fileId - File ID of the tab to keep open
   * @param {Array} openFiles - Array of open file objects
   * @param {string|null} activeFileId - Currently active file ID
   * @param {Function} setOpenFiles - React state setter for openFiles
   * @param {Function} setUnsavedFiles - React state setter for unsavedFiles
   * @param {Function} setActiveFileId - React state setter for activeFileId
   * @param {Object} manager - Workspace manager instance
   * @returns {Promise<Object>} Result with operation status
   */
  async closeOthers(
    fileId,
    openFiles = [],
    activeFileId = null,
    setOpenFiles,
    setUnsavedFiles,
    setActiveFileId,
    manager
  ) {
    try {
      // Validate fileId
      if (!fileId || typeof fileId !== "string") {
        return this.returnError({}, "Valid file ID required");
      }

      // Validate openFiles array
      if (!Array.isArray(openFiles)) {
        return this.returnError({}, "openFiles must be an array");
      }

      // Validate state setters
      if (typeof setOpenFiles !== "function") {
        return this.returnError({}, "setOpenFiles function is required");
      }
      if (typeof setUnsavedFiles !== "function") {
        return this.returnError({}, "setUnsavedFiles function is required");
      }
      if (typeof setActiveFileId !== "function") {
        return this.returnError({}, "setActiveFileId function is required");
      }

      const targetFile = openFiles.find(
        (f) => f.id === fileId || f.stableId === fileId
      );
      if (!targetFile) {
        return this.returnError(
          {},
          `File with ID ${fileId} not found in open files`
        );
      }

      const filesToClose = openFiles
        .filter((f) => f.id !== fileId && f.stableId !== fileId)
        .map((f) => f.id);

      // Close the specified files
      setOpenFiles((prev) =>
        prev.filter((f) => f.id === fileId || f.stableId === fileId)
      );

      // Remove from unsaved files
      setUnsavedFiles((prev) => {
        const newSet = new Set(prev);
        filesToClose.forEach((id) => {
          newSet.delete(id);
          // Also check stableId
          const fileToRemove = openFiles.find(
            (f) => f.id === id || f.stableId === id
          );
          if (fileToRemove?.stableId) {
            newSet.delete(fileToRemove.stableId);
          }
        });
        return newSet;
      });

      // Keep the target file active
      setActiveFileId(fileId);
      if (targetFile && manager) {
        await manager.selectFile(targetFile);
      }

      logger.log(
        {
          tags: "tools|closetab|info",
          color1: "lime",
        },
        `Closing other tabs, keeping: ${targetFile.name || fileId}`
      );

      return this.returnSuccess(
        {
          fileId,
          filesClosed: filesToClose.length,
          action: "close-others",
        },
        "Other tabs closed successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|closetab|error",
          color1: "red",
        },
        `Failed to close other tabs: ${fileId}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Close all tabs to the right of the specified tab
   * @param {string} fileId - File ID of the tab (closes all tabs to the right)
   * @param {Array} openFiles - Array of open file objects
   * @param {string|null} activeFileId - Currently active file ID
   * @param {Function} setOpenFiles - React state setter for openFiles
   * @param {Function} setUnsavedFiles - React state setter for unsavedFiles
   * @param {Function} setActiveFileId - React state setter for activeFileId
   * @param {Object} manager - Workspace manager instance
   * @returns {Promise<Object>} Result with operation status
   */
  async closeRight(
    fileId,
    openFiles = [],
    activeFileId = null,
    setOpenFiles,
    setUnsavedFiles,
    setActiveFileId,
    manager
  ) {
    try {
      // Validate fileId
      if (!fileId || typeof fileId !== "string") {
        return this.returnError({}, "Valid file ID required");
      }

      // Validate openFiles array
      if (!Array.isArray(openFiles)) {
        return this.returnError({}, "openFiles must be an array");
      }

      // Validate state setters
      if (typeof setOpenFiles !== "function") {
        return this.returnError({}, "setOpenFiles function is required");
      }
      if (typeof setUnsavedFiles !== "function") {
        return this.returnError({}, "setUnsavedFiles function is required");
      }
      if (typeof setActiveFileId !== "function") {
        return this.returnError({}, "setActiveFileId function is required");
      }

      const targetFileIndex = openFiles.findIndex(
        (f) => f.id === fileId || f.stableId === fileId
      );
      if (targetFileIndex === -1) {
        return this.returnError(
          {},
          `File with ID ${fileId} not found in open files`
        );
      }

      // Close all files after the target file
      const filesToClose = openFiles
        .slice(targetFileIndex + 1)
        .map((f) => f.id);

      // Close the specified files
      setOpenFiles((prev) =>
        prev.filter(
          (f) =>
            !filesToClose.includes(f.id) && !filesToClose.includes(f.stableId)
        )
      );

      // Remove from unsaved files
      setUnsavedFiles((prev) => {
        const newSet = new Set(prev);
        filesToClose.forEach((id) => {
          newSet.delete(id);
          // Also check stableId
          const fileToRemove = openFiles.find(
            (f) => f.id === id || f.stableId === id
          );
          if (fileToRemove?.stableId) {
            newSet.delete(fileToRemove.stableId);
          }
        });
        return newSet;
      });

      // Determine new active file if the current active is being closed
      const isActiveFileBeingClosed =
        activeFileId &&
        (filesToClose.includes(activeFileId) ||
          openFiles.some(
            (f) =>
              (f.id === activeFileId || f.stableId === activeFileId) &&
              (filesToClose.includes(f.id) || filesToClose.includes(f.stableId))
          ));

      if (isActiveFileBeingClosed) {
        const remainingFiles = openFiles.filter(
          (f) =>
            !filesToClose.includes(f.id) && !filesToClose.includes(f.stableId)
        );
        if (remainingFiles.length > 0) {
          const newActiveId = remainingFiles[remainingFiles.length - 1].id;
          setActiveFileId(newActiveId);
          const file = remainingFiles.find((f) => f.id === newActiveId);
          if (file && manager) {
            await manager.selectFile(file);
          }
        } else {
          setActiveFileId(null);
        }
      } else {
        // Active file is NOT being closed, keep it active
        if (activeFileId) {
          const stillActiveFile = openFiles.find(
            (f) => f.id === activeFileId || f.stableId === activeFileId
          );
          if (stillActiveFile && manager) {
            await manager.selectFile(stillActiveFile);
          }
        }
      }

      logger.log(
        {
          tags: "tools|closetab|info",
          color1: "lime",
        },
        `Closing ${filesToClose.length} tabs to the right of: ${
          openFiles[targetFileIndex].name || fileId
        }`
      );

      return this.returnSuccess(
        {
          fileId,
          filesClosed: filesToClose.length,
          action: "close-right",
        },
        "Tabs to the right closed successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|closetab|error",
          color1: "red",
        },
        `Failed to close tabs to the right: ${fileId}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Close all tabs with saved (no unsaved changes)
   * @param {Array} openFiles - Array of open file objects
   * @param {string|null} activeFileId - Currently active file ID
   * @param {Array|Set} unsavedFiles - Array or Set of unsaved file IDs
   * @param {Function} setOpenFiles - React state setter for openFiles
   * @param {Function} setUnsavedFiles - React state setter for unsavedFiles
   * @param {Function} setActiveFileId - React state setter for activeFileId
   * @param {Object} manager - Workspace manager instance
   * @returns {Promise<Object>} Result with operation status
   */
  async closeSaved(
    openFiles = [],
    activeFileId = null,
    unsavedFiles = [],
    setOpenFiles,
    setUnsavedFiles,
    setActiveFileId,
    manager
  ) {
    try {
      // Validate openFiles array
      if (!Array.isArray(openFiles)) {
        return this.returnError({}, "openFiles must be an array");
      }

      // Validate state setters
      if (typeof setOpenFiles !== "function") {
        return this.returnError({}, "setOpenFiles function is required");
      }
      if (typeof setUnsavedFiles !== "function") {
        return this.returnError({}, "setUnsavedFiles function is required");
      }
      if (typeof setActiveFileId !== "function") {
        return this.returnError({}, "setActiveFileId function is required");
      }

      // Convert unsavedFiles array to Set for easier lookup
      const unsavedFilesSet = Array.isArray(unsavedFiles)
        ? new Set(unsavedFiles)
        : unsavedFiles instanceof Set
          ? unsavedFiles
          : new Set();

      const filesToClose = openFiles
        .filter(
          (f) => !unsavedFilesSet.has(f.id) && !unsavedFilesSet.has(f.stableId)
        )
        .map((f) => f.id);

      // Close the specified files
      setOpenFiles((prev) => prev.filter((f) => !filesToClose.includes(f.id)));

      // Remove from unsaved files
      setUnsavedFiles((prev) => {
        const newSet = new Set(prev);
        filesToClose.forEach((id) => {
          newSet.delete(id);
          // Also check stableId
          const fileToRemove = openFiles.find(
            (f) => f.id === id || f.stableId === id
          );
          if (fileToRemove?.stableId) {
            newSet.delete(fileToRemove.stableId);
          }
        });
        return newSet;
      });

      // If active file is being closed, find a new active file (prefer unsaved)
      if (filesToClose.includes(activeFileId)) {
        const remainingFiles = openFiles.filter(
          (f) => !filesToClose.includes(f.id)
        );
        if (remainingFiles.length > 0) {
          // Prefer the last unsaved file, or just the last file
          const newActiveId = remainingFiles[remainingFiles.length - 1].id;
          setActiveFileId(newActiveId);
          const file = remainingFiles.find((f) => f.id === newActiveId);
          if (file && manager) {
            await manager.selectFile(file);
          }
        } else {
          setActiveFileId(null);
        }
      } else {
        // Active file is NOT being closed, keep it active
        if (activeFileId) {
          const stillActiveFile = openFiles.find(
            (f) => f.id === activeFileId || f.stableId === activeFileId
          );
          if (stillActiveFile && manager) {
            await manager.selectFile(stillActiveFile);
          }
        }
      }

      logger.log(
        {
          tags: "tools|closetab|info",
          color1: "lime",
        },
        `Closing ${filesToClose.length} saved tabs`
      );

      return this.returnSuccess(
        {
          filesClosed: filesToClose.length,
          action: "close-saved",
        },
        "Saved tabs closed successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|closetab|error",
          color1: "red",
        },
        "Failed to close saved tabs",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Helper method to find the first file that needs confirmation
   * @param {Array} files - Array of file objects to check
   * @param {Set} unsavedFilesSet - Set of unsaved file IDs
   * @returns {Object|null} First file that needs confirmation, or null
   */
  findFirstUnsavedFile(files, unsavedFilesSet) {
    for (const file of files) {
      if (unsavedFilesSet.has(file.id) || unsavedFilesSet.has(file.stableId)) {
        return file;
      }
    }
    return null;
  }

  /**
   * Close all tabs with sequential confirmation handling
   * @param {Array} openFiles - Array of open file objects
   * @param {string|null} activeFileId - Currently active file ID
   * @param {Set|Array} unsavedFiles - Set or Array of unsaved file IDs
   * @param {Function} setOpenFiles - React state setter for openFiles
   * @param {Function} setUnsavedFiles - React state setter for unsavedFiles
   * @param {Function} setActiveFileId - React state setter for activeFileId
   * @param {Object} manager - Workspace manager instance
   * @param {Function} getConfirmation - Callback that receives file and returns Promise<'save' | 'discard' | 'cancel'>
   * @param {Function} onSaveFile - Optional callback to save a file
   * @returns {Promise<Object>} Result with operation status
   */
  async closeAll(
    openFiles = [],
    activeFileId = null,
    unsavedFiles = new Set(),
    setOpenFiles,
    setUnsavedFiles,
    setActiveFileId,
    manager,
    getConfirmation = null,
    onSaveFile = null
  ) {
    try {
      // Validate openFiles array
      if (!Array.isArray(openFiles)) {
        return this.returnError({}, "openFiles must be an array");
      }

      // Validate state setters
      if (typeof setOpenFiles !== "function") {
        return this.returnError({}, "setOpenFiles function is required");
      }
      if (typeof setUnsavedFiles !== "function") {
        return this.returnError({}, "setUnsavedFiles function is required");
      }
      if (typeof setActiveFileId !== "function") {
        return this.returnError({}, "setActiveFileId function is required");
      }

      // Convert unsavedFiles to Set if needed
      const unsavedFilesSet = unsavedFiles instanceof Set 
        ? unsavedFiles 
        : new Set(Array.isArray(unsavedFiles) ? unsavedFiles : []);

      // Process files one at a time
      const filesToClose = [...openFiles];
      const closedFileIds = [];
      let currentOpenFiles = [...openFiles];
      let currentActiveFileId = activeFileId;

      for (const file of filesToClose) {
        // Check if file still exists (might have been closed already)
        const fileStillOpen = currentOpenFiles.some(f => f.id === file.id || f.stableId === file.id);
        if (!fileStillOpen) {
          continue; // Already closed
        }

        // Check for unsaved changes
        const hasUnsaved = unsavedFilesSet.has(file.id) || unsavedFilesSet.has(file.stableId);

        if (hasUnsaved && getConfirmation && typeof getConfirmation === 'function') {
          // Request confirmation from the callback
          try {
            const userChoice = await getConfirmation(file);
            
            if (userChoice === 'cancel') {
              // User cancelled, stop closing
              return this.returnSuccess(
                {
                  cancelled: true,
                  filesClosed: closedFileIds.length,
                  action: "close-all",
                },
                "Close operation cancelled"
              );
            }

            if (userChoice === 'save') {
              // Save the file
              if (onSaveFile && typeof onSaveFile === 'function') {
                try {
                  await onSaveFile(file);
                } catch (error) {
                  logger.warn(
                    { tags: "tools|closetab|save", color1: "yellow" },
                    `Failed to save file before closing: ${file.name}`,
                    error
                  );
                }
              }
            }
            // If userChoice is 'discard', just continue without saving
          } catch (error) {
            logger.error(
              { tags: "tools|closetab|confirm", color1: "red" },
              `Error getting confirmation for file: ${file.name}`,
              error
            );
            // Continue with closing on error
          }
        }

        // Close the file
        const fileId = file.id;
        closedFileIds.push(fileId);

        // Update openFiles state
        setOpenFiles((prev) => prev.filter((f) => f.id !== fileId));
        currentOpenFiles = currentOpenFiles.filter((f) => f.id !== fileId);

        // Update unsavedFiles state
        setUnsavedFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          if (file.stableId) {
            newSet.delete(file.stableId);
          }
          return newSet;
        });

        // Update active file if needed
        if (currentActiveFileId === fileId) {
          if (currentOpenFiles.length > 0) {
            const newActiveId = currentOpenFiles[currentOpenFiles.length - 1].id;
            setActiveFileId(newActiveId);
            currentActiveFileId = newActiveId;
            const newActiveFile = currentOpenFiles.find((f) => f.id === newActiveId);
            if (newActiveFile && manager) {
              await manager.selectFile(newActiveFile);
            }
          } else {
            setActiveFileId(null);
            currentActiveFileId = null;
          }
        }
      }

      logger.log(
        {
          tags: "tools|closetab|info",
          color1: "lime",
        },
        `Closed all ${closedFileIds.length} tabs`
      );

      return this.returnSuccess(
        {
          filesClosed: closedFileIds.length,
          action: "close-all",
        },
        "All tabs closed successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|closetab|error",
          color1: "red",
        },
        "Failed to close all tabs",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Determine the new active file ID after closing a file
   * @param {string} closedFileId - ID of the file being closed
   * @param {Array} openFiles - Array of open file objects
   * @param {string|null} currentActiveFileId - Currently active file ID
   * @returns {string|null} New active file ID
   */
  getNewActiveFileId(closedFileId, openFiles, currentActiveFileId) {
    // If the closed file wasn't active, keep the current active file
    const closedFile = openFiles.find(
      (f) => f.id === closedFileId || f.stableId === closedFileId
    );
    if (!closedFile) {
      return currentActiveFileId;
    }

    // Check if the closed file was the active one
    const wasActive =
      currentActiveFileId &&
      (currentActiveFileId === closedFileId ||
        closedFile.id === currentActiveFileId ||
        closedFile.stableId === currentActiveFileId);

    // If the closed file wasn't active, keep the current active file
    if (!wasActive) {
      return currentActiveFileId;
    }

    const closedFileIndex = openFiles.findIndex(
      (f) => f.id === closedFileId || f.stableId === closedFileId
    );
    const remainingFiles = openFiles.filter(
      (f) => f.id !== closedFileId && f.stableId !== closedFileId
    );

    if (remainingFiles.length === 0) {
      return null;
    }

    // Try to select the tab to the left, or the last tab if closing the first one
    if (closedFileIndex > 0) {
      const leftFile = openFiles[closedFileIndex - 1];
      return leftFile.id;
    } else {
      // Closing the first tab, select the new first tab
      return remainingFiles[0].id;
    }
  }
}
